import { IApiQueryResponse } from '@llws/api-common';
import { dispatch } from '../../store';
import { commissionsPayableListReceived, salesRepListReceived, setLoadedState } from './commissionsSlice';
import { SalesReps } from '@llws/typeorm-entities';
import { apiFetch, IApiQuerySingularResponse, typedApiFetch } from '../../utils';
import { ICommissionRecord } from './interfaces';
import { showMessage } from '../messaging/actions';
import { REACT_APP_API_ROOT_URI } from '../../../App';

/**
 * Fetches the list of sales reps from the commissions API and stores them in Redux.
 *
 * @param source - Data source to query. Defaults to `'legacy'`.
 *   Pass `'hydra'` to load reps from the Hydra billing system instead.
 */
export const loadSalesReps = async (source: 'legacy' | 'hydra' = 'legacy') => {
  const url = `${REACT_APP_API_ROOT_URI}/commissions/sales_reps?source=${source}`;
  dispatch(setLoadedState('loading'));
  const response = await apiFetch(url);
  const responseData = await response.json() as IApiQueryResponse<SalesReps>;
  dispatch(salesRepListReceived(responseData)); // TODO: Error handling?
  dispatch(setLoadedState('loaded'));
};

/**
 * Fetches commission records for one or more sales reps and stores the combined
 * result in Redux. The backend handles combining records when multiple IDs are
 * supplied (via the `salesRepIds` query param), so only one request is made.
 * When multiple reps are requested, the backend tags each record with `salesRepId`
 * and `salesRepName`, enabling the table to render distinguishing **Sales Rep ID**
 * and **Sales Rep** columns.
 *
 * @param ids - A single sales rep ID or an array of IDs.
 * @param query - Optional query-string suffix appended to the API URL
 *   (e.g. `'/payable?invoiceStates=…&paid=false'`). Defaults to an empty string.
 */
export const loadCommissions = async (ids: number | number[], query: string = '') => {
  const repIds = (Array.isArray(ids) ? ids : [ids]).filter(id => id > 0);

  dispatch(setLoadedState('loading'));

  if (repIds.length === 0) {
    dispatch(commissionsPayableListReceived({ data: [] } as unknown as IApiQueryResponse<ICommissionRecord>));
    dispatch(setLoadedState('loaded'));
    return;
  }

  // The path param is required by the route but is superseded by salesRepIds when multiple reps are selected.
  const salesRepIdsParam = repIds.length > 1 ? `&salesRepIds=${repIds.join(',')}` : '';
  const response = await apiFetch(
    `${REACT_APP_API_ROOT_URI}/commissions/sales_reps/${repIds[0]}${query}${salesRepIdsParam}`,
  );
  const data = await response.json() as IApiQueryResponse<ICommissionRecord>;
  dispatch(commissionsPayableListReceived(
    { data: (data.data ?? []) as ICommissionRecord[] } as unknown as IApiQueryResponse<ICommissionRecord>,
  ));
  dispatch(setLoadedState('loaded'));
};

/**
 * Dispatches the `cleanupCommissionsPayable` action to the API, which sets the
 * `commissionPaidDate` on matching non-monthly-subscription commissions and clawbacks to a
 * far-future date, effectively removing them from the payable view.
 *
 * On completion, a Snackbar notification is shown to the user with the server response.
 * The severity is `'error'` when the API returns `status: 'error'`, otherwise `'info'`.
 *
 * @param salesRepId - ID of the sales rep whose payable commissions should be cleaned up.
 * @param startInvoiceDate - ISO date string (e.g. `'2024-06-01'`); only records with an
 *   invoice date on or after this value will be affected.
 */
export const cleanupCommissionsPayable = async (salesRepId: number, startInvoiceDate: string) => {
  const body = {
    action: 'cleanupCommissionsPayable',
    parameters: {
      salesRepId,
      startInvoiceDate,
    },
  };
  const url = `${REACT_APP_API_ROOT_URI}/actions`;
  const response = await apiFetch(url, { method: 'POST', body: JSON.stringify(body) });
  const responseData = await response.json() as IApiQuerySingularResponse<{ actionResponse: { status: string; message: string } }>;
  const { status, message } = responseData.data?.actionResponse ?? { status: 'error', message: 'Unexpected error: no response data.' };
  const severity = status === 'error' ? 'error' : 'info';
  showMessage({ message, severity });
};

/**
 * Marks the supplied commission records as paid by dispatching the
 * `markCommissionsAsPaid` action to the API.
 *
 * Shows an `info` Snackbar with the server's confirmation message on success,
 * then invokes `callbackFn` to allow the caller to refresh or reset state.
 *
 * @param commissionRecords - Array of commission records to mark as paid.
 * @param callbackFn - Callback invoked after the API response is processed.
 */
export const markCommissionsAsPaid = async (commissionRecords: ICommissionRecord[], callbackFn: Function) => {
  const body = {
    action: "markCommissionsAsPaid",
    parameters: {
      commissionRecords,
    },
  };
  const url = `${REACT_APP_API_ROOT_URI}/actions`;
  const response = await apiFetch(url, { method: 'POST', body: JSON.stringify(body) });
  const responseData = await response.json() as IApiQuerySingularResponse<{ actionResponse: { message: string } }>;
  showMessage({ message: `${responseData.data.actionResponse.message}`, severity: 'info' });
  callbackFn();
};

/**
 * Uses an uploaded commissions paid file to dispatch an action that updates the
 * `commissionPaidDate` on the Hydra records.
 *
 * If the action returns `status` of error, an alert will be shown to the user.
 * If successful, an `info` variant Snackbar will be shown to the user, indicating
 * that the commissions are being updated.
 * @param commissionRecordsFile - A CSV file that holds the paid commissions data.
 */
export const markCommissionsAsPaidByFile = async (commissionRecordsFile: File) => {
  const formData = new FormData();
  formData.append("action", "markCommissionsAsPaidByFile");
  formData.append("commissionsUpload", commissionRecordsFile );
  const url = `${REACT_APP_API_ROOT_URI}/actions`;
  const response = await typedApiFetch<IApiQuerySingularResponse<
  {
    actionResponse: { status: string, message: string }
  }
  >>(url, {
    method: 'POST',
    body: formData,
  }, { defaultJsonContent: false });
  const responseData = await response.json();

  const { status, message } = responseData.data.actionResponse;

  const severity = status === 'error' ? 'error' : 'info';
  const asAlert = status === 'error';

  showMessage({ message, severity, asAlert });
};
